import { ArrowUp, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { errorMessage } from '@/lib/api/errors'
import { type HelpCategory, useHelpCategoryAction } from './api'

/** Owners and admins: the help center's sections, in the order they're shown. */
export function SectionsDialog({
  open,
  onOpenChange,
  categories,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  categories: HelpCategory[]
}) {
  const action = useHelpCategoryAction()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // Swap with the one above, then save every position that changed.
  const moveUp = async (i: number) => {
    const order = [...categories]
    const above = order[i - 1]
    const current = order[i]
    if (!above || !current) return
    order[i - 1] = current
    order[i] = above
    try {
      for (const [position, c] of order.entries()) {
        if (c.position !== position) {
          await action.mutateAsync({
            action: 'update',
            id: c.id,
            name: c.name,
            description: c.description,
            position,
          })
        }
      }
      toast.success('Order saved.')
    } catch (err) {
      toast.error(errorMessage(err))
    }
  }

  const run = (input: Parameters<typeof action.mutate>[0], done: string, after?: () => void) =>
    action.mutate(input, {
      onSuccess: () => {
        toast.success(done)
        after?.()
      },
      onError: (err) => toast.error(errorMessage(err)),
    })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        title="Sections"
        description="Help center topics, shown in this order. Removing one keeps its articles, without a section."
      >
        <ol className="grid gap-2">
          {categories.map((c, i) => (
            <li
              key={c.id}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[13px]"
            >
              <span className="w-5 text-ink-subtle">{i + 1}</span>
              <span className="flex-1 font-medium text-ink">
                {c.name} <span className="font-normal text-ink-subtle">· {c.articles} articles</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={i === 0 || action.isPending}
                aria-label={`Move ${c.name} up`}
                onClick={() => void moveUp(i)}
              >
                <ArrowUp />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${c.name}`}
                onClick={() => run({ action: 'delete', id: c.id }, `${c.name} removed.`)}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ol>
        <form
          className="grid gap-3 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault()
            run(
              { action: 'create', name: name.trim(), description: description.trim() || null },
              'Section added.',
              () => {
                setName('')
                setDescription('')
              },
            )
          }}
        >
          <Field label="New section" htmlFor="section-name" required>
            <Input
              id="section-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Local SEO"
              required
            />
          </Field>
          <Field
            label="Description"
            htmlFor="section-description"
            hint="One line, shown on the help center's home page."
          >
            <Input
              id="section-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <div>
            <Button type="submit" loading={action.isPending}>
              Add section
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
